import { useEffect } from 'react';
import { useInfiniteQuery } from 'react-query';

import { communityFeedApi } from 'src/Services/API';
import { CommentDto } from 'src/Services/API/generated';

const useFetchCommunityFeedItemComments = (feedItemId: string, initialComments?: CommentDto[]) => {
  const query = useInfiniteQuery({
    queryKey: ['fetch-community-feed-item-comments', feedItemId],
    queryFn: async ({ pageParam = null }) => {
      return (await communityFeedApi.communityFeedControllerListComments(feedItemId, 20, pageParam))
        .data;
    },
    getNextPageParam: lastPage => lastPage.endCursor ?? undefined,

    // The feed response embeds the comments of each item: seed the query with them
    // so displaying comments costs no extra request. Mutations still refresh the
    // query through the classic endpoint.
    initialData: initialComments
      ? { pages: [{ items: initialComments, endCursor: null }], pageParams: [null] }
      : undefined,

    // Cache the comments for a minute so we are up to date
    cacheTime: 1 * 60 * 1000,
    staleTime: 1 * 60 * 1000,
  });

  // Fetch all the pages automatically
  useEffect(() => {
    const loadAllPages = async () => {
      if (query.hasNextPage && !query.isFetchingNextPage) {
        await query.fetchNextPage();
      }
    };

    loadAllPages();
  }, [query.hasNextPage, query.isFetchingNextPage, query.fetchNextPage]);

  return query;
};

export default useFetchCommunityFeedItemComments;
